import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/* Listens for the browser's beforeinstallprompt event and shows a soft prompt
 * in the bottom-right when the app is installable but not yet installed.
 * Dismissed state persists in localStorage for 30 days.
 */
const DISMISS_KEY = "cor_install_dismissed";
const DISMISS_DAYS = 30;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // If user already dismissed recently, never show.
    try {
      const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (ts && (Date.now() - ts) < DISMISS_DAYS * 86_400_000) return;
    } catch { /* ignore */ }

    // If already running as installed PWA, suppress.
    const standalone = window.matchMedia?.("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    if (standalone) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Give the page a beat to load before nagging.
      setTimeout(() => setVisible(true), 3000);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installed = () => { setVisible(false); setDeferred(null); };
    window.addEventListener("appinstalled", installed);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-6 left-6 z-[55] paper p-4 max-w-xs shadow-xl animate-in fade-in slide-in-from-bottom-4 duration-300"
      role="dialog"
    >
      <button
        data-testid="install-dismiss"
        onClick={dismiss}
        className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
      <div className="flex items-start gap-3 mb-3">
        <Download size={16} className="mt-0.5 text-[hsl(var(--primary))]" />
        <div>
          <div className="serif text-lg text-foreground leading-tight">Install Cor·relate</div>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            Keep the archive one tap away — works offline, no app store.
          </p>
        </div>
      </div>
      <button
        data-testid="install-confirm"
        onClick={install}
        className="btn-gold w-full"
      >
        Install
      </button>
    </div>
  );
}
